import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { CheckCircle2, Download, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const CelebrationModal = ({ isOpen, onClose, stepsCompleted, pathName }) => {
  const { session } = useAuth();
  const navigate = useNavigate();

  const handleDownloadPdf = async () => {
    try {
      const token = session?.access_token;
      if (!token) {
        toast.error('Please sign in to download');
        return;
      }
      const response = await fetch(`${API}/outputs/me/export-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'easy-street-funnels-export.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('PDF downloaded!');
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.detail || 'Download failed');
      }
    } catch (err) {
      process.env.NODE_ENV === "development" && console.error('Export failed:', err);
      toast.error('Download failed. Please try again.');
    }
  };

  const handleStartNewPath = () => {
    onClose();
    navigate('/onboarding');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="w-full max-w-md bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-2xl relative"
          onClick={(e) => e.stopPropagation()}
          data-testid="celebration-modal"
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="text-center">
            {/* Success icon */}
            <div className="w-16 h-16 bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>

            <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2" data-testid="celebration-title">
              Path Complete!
            </h2>
            <p className="text-muted-foreground text-sm mb-1">
              You crushed it. Every step of the <span className="text-foreground font-medium">{pathName}</span> is done.
            </p>
            <p className="text-xs text-muted-foreground mb-6">
              {stepsCompleted} steps completed
            </p>

            {/* Actions */}
            <div className="space-y-3">
              <Button
                onClick={handleDownloadPdf}
                className="w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="download-all-btn"
              >
                <Download className="mr-2 h-4 w-4" />
                Download All My Content
              </Button>

              <Button
                onClick={handleStartNewPath}
                variant="outline"
                className="w-full h-11"
                data-testid="new-path-btn"
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Start a New Path
              </Button>

              <Button
                onClick={onClose}
                variant="ghost"
                className="w-full h-9 text-sm text-muted-foreground"
              >
                Back to Dashboard
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
